// src/store/slices/reviewsSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import reviewService from '../../services/reviewService';

interface ReviewItem {
  _id: string;
  client?: { _id: string; name: string };
  stylist?: { _id: string; name: string };
  rating?: { overall: number };
  review?: { text: string };
  createdAt?: string;
  [key: string]: any;
}

interface ReviewsState {
  stylistReviews: Record<string, ReviewItem[]>;
  userReviews: ReviewItem[];
  isLoading: boolean;
  error: string | null;
}

const initialState: ReviewsState = {
  stylistReviews: {},
  userReviews: [],
  isLoading: false,
  error: null,
};

export const fetchStylistReviews = createAsyncThunk(
  'reviews/fetchByStylist',
  async (stylistId: string, { rejectWithValue }) => {
    try {
      const result = await reviewService.getStylistReviews(stylistId);
      const reviews: ReviewItem[] = (result as any).reviews ?? (result as any).data ?? (Array.isArray(result) ? result : []);
      return { stylistId, reviews };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch reviews');
    }
  }
);

export const fetchUserReviews = createAsyncThunk(
  'reviews/fetchByUser',
  async (userId: string, { rejectWithValue }) => {
    try {
      const result = await reviewService.getClientReviews(userId);
      const reviews: ReviewItem[] = (result as any).reviews ?? (result as any).data ?? [];
      return reviews;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch user reviews');
    }
  }
);

export const createReview = createAsyncThunk(
  'reviews/create',
  async (
    {
      stylistId,
      appointmentId,
      rating,
      comment,
    }: {
      stylistId: string;
      appointmentId: string;
      rating: number;
      comment: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const review = await reviewService.createReview({
        appointmentId,
        reviewType: 'stylist-review',
        rating: { overall: rating, aspects: {} },
        review: comment,
      });
      return { stylistId, review };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to create review'
      );
    }
  }
);

export const deleteReview = createAsyncThunk(
  'reviews/delete',
  async ({ reviewId, stylistId }: { reviewId: string; stylistId: string }, { rejectWithValue }) => {
    try {
      await reviewService.deleteReview(reviewId);
      return { stylistId, reviewId };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete review');
    }
  }
);

const reviewsSlice = createSlice({
  name: 'reviews',
  initialState,
  reducers: {
    clearReviewsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStylistReviews.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchStylistReviews.fulfilled,
        (state, action: PayloadAction<{ stylistId: string; reviews: ReviewItem[] }>) => {
          state.isLoading = false;
          state.stylistReviews[action.payload.stylistId] = action.payload.reviews;
        }
      )
      .addCase(fetchStylistReviews.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchUserReviews.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserReviews.fulfilled, (state, action: PayloadAction<ReviewItem[]>) => {
        state.isLoading = false;
        state.userReviews = action.payload;
      })
      .addCase(fetchUserReviews.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(createReview.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        createReview.fulfilled,
        (state, action: PayloadAction<{ stylistId: string; review: ReviewItem }>) => {
          state.isLoading = false;
          if (!state.stylistReviews[action.payload.stylistId]) {
            state.stylistReviews[action.payload.stylistId] = [];
          }
          state.stylistReviews[action.payload.stylistId].unshift(action.payload.review);
          state.userReviews.unshift(action.payload.review);
        }
      )
      .addCase(createReview.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(deleteReview.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        deleteReview.fulfilled,
        (state, action: PayloadAction<{ stylistId: string; reviewId: string }>) => {
          state.isLoading = false;
          if (state.stylistReviews[action.payload.stylistId]) {
            state.stylistReviews[action.payload.stylistId] = state.stylistReviews[
              action.payload.stylistId
            ].filter((r) => r._id !== action.payload.reviewId);
          }
          state.userReviews = state.userReviews.filter((r) => r._id !== action.payload.reviewId);
        }
      )
      .addCase(deleteReview.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearReviewsError } = reviewsSlice.actions;
export default reviewsSlice.reducer;
